/**
 * Admin Resource Catalog — the single source of truth for admin-panel PBAC.
 *
 * Every grantable admin resource lives here, once. Three consumers derive from it,
 * so they cannot drift:
 * - the frontend policy picker renders this tree (what a policy can grant),
 * - the frontend sidebar (`ADMIN_NAVIGATION`) decorates the navigable items with a
 *   route + icon,
 * - the backend resource seed flattens it into the registry.
 *
 * And every backend `@RequireAdminPermission(<resource>)` string MUST be a path in
 * this catalog — enforced by a drift test — so "guarded" always implies "grantable"
 * (no godmode-only skeletons).
 *
 * A resource path is colon-delimited: `admin:<item>`, `admin:<item>:<action>`,
 * `admin:<item>:<block>`, `admin:<item>:<block>:<action>`. Presentation (href, icon,
 * sidebar section) is NOT here — it is a frontend concern layered on in
 * `admin-navigation.ts`. Categories group the tree in the picker and the sidebar.
 */

import { ADMIN_RESOURCE_CATALOG_ADMIN } from "./resource-catalog.admin";
import { ADMIN_RESOURCE_CATALOG_OVERVIEW } from "./resource-catalog.overview";
import { ADMIN_RESOURCE_CATALOG_SYSTEM_CORE } from "./resource-catalog.system-core";
import { ADMIN_RESOURCE_CATALOG_SYSTEM_INTEGRATIONS } from "./resource-catalog.system-integrations";
import type { AdminResourceCategory, AdminResourceItemDef } from "./resource-catalog.types";

export type {
  AdminResourceActionDef,
  AdminResourceBlockDef,
  AdminResourceCategory,
  AdminResourceItemDef
} from "./resource-catalog.types";
export { ADMIN_RESOURCE_CATEGORY_ORDER } from "./resource-catalog.types";

/**
 * The catalog. Item order within a category is the display order. Sidebar presence
 * and grantability are independent:
 *
 * - a presentation entry in `admin-navigation.ts` → appears in the sidebar;
 * - no presentation entry → grantable-only (backend endpoint / sub-feature with no
 *   page of its own);
 * - `navOnly: true` → in the sidebar but NOT grantable, because access is gated by
 *   another resource.
 */
export const ADMIN_RESOURCE_CATALOG: readonly AdminResourceItemDef[] = [
  ...ADMIN_RESOURCE_CATALOG_OVERVIEW,
  ...ADMIN_RESOURCE_CATALOG_SYSTEM_CORE,
  ...ADMIN_RESOURCE_CATALOG_SYSTEM_INTEGRATIONS,
  ...ADMIN_RESOURCE_CATALOG_ADMIN
] as const;

/** A flattened catalog entry with its full resource path and hierarchy metadata. */
export interface AdminResourcePath {
  path: string;
  name: string;
  description: string;
  category: AdminResourceCategory;
  resourceType: "NAV" | "BLOCK" | "ACTION";
  parentPath: string | null;
  sortOrder: number;
}

/**
 * Is this node offered as a grant?
 *
 * ONE definition, because there are two walkers over this catalog and they are not
 * the same code: `flattenAdminResourceCatalog` below (the backend seed, the
 * `AdminResourceKey` union, the drift tests) and `buildTreeFromConfig` in the
 * frontend `ResourcePicker`, which recurses the raw catalog itself so it can carry
 * each node's max-permission ceiling. They must agree on what is grantable or the
 * picker offers a checkbox the registry has no row for — which is how `navOnly`
 * came to be honoured in two places by two separate `if`s.
 */
export function isGrantableResource(node: { navOnly?: boolean; unserved?: string }): boolean {
  return node.navOnly !== true && node.unserved === undefined;
}

/**
 * Flatten the catalog into resource paths, depth-first in declaration order.
 * This is what the backend seed and the drift test consume.
 */
export function flattenAdminResourceCatalog(
  catalog: readonly AdminResourceItemDef[] = ADMIN_RESOURCE_CATALOG
): AdminResourcePath[] {
  const resources: AdminResourcePath[] = [];
  let sortOrder = 0;

  for (const item of catalog) {
    // Nav-only sections are gated by another resource, and `unserved` nodes are
    // gated by nothing at all — neither is grantable, so skip the whole subtree.
    // This is what keeps the picker from offering a grant that does nothing.
    if (!isGrantableResource(item)) continue;

    const basePath = `admin:${item.id}`;
    resources.push({
      path: basePath,
      name: item.name,
      description: item.description,
      category: item.category,
      resourceType: "NAV",
      parentPath: null,
      sortOrder: sortOrder++
    });

    for (const action of item.actions ?? []) {
      if (!isGrantableResource(action)) continue;
      resources.push({
        path: `${basePath}:${action.id}`,
        name: action.name,
        description: action.description,
        category: item.category,
        resourceType: "ACTION",
        parentPath: basePath,
        sortOrder: sortOrder++
      });
    }

    for (const block of item.blocks ?? []) {
      if (!isGrantableResource(block)) continue;
      const blockPath = `${basePath}:${block.id}`;
      resources.push({
        path: blockPath,
        name: block.name,
        description: block.description,
        category: item.category,
        resourceType: "BLOCK",
        parentPath: basePath,
        sortOrder: sortOrder++
      });

      for (const action of block.actions ?? []) {
        if (!isGrantableResource(action)) continue;
        resources.push({
          path: `${blockPath}:${action.id}`,
          name: action.name,
          description: action.description,
          category: item.category,
          resourceType: "ACTION",
          parentPath: blockPath,
          sortOrder: sortOrder++
        });
      }
    }
  }

  return resources;
}

/** The full set of grantable resource paths — every guard string must be in here. */
export function adminResourcePathSet(
  catalog: readonly AdminResourceItemDef[] = ADMIN_RESOURCE_CATALOG
): Set<string> {
  return new Set(flattenAdminResourceCatalog(catalog).map((r) => r.path));
}
